/**
 * ClapButton — Medium/Substack-style multi-clap.
 *
 * Behaviour:
 *   • Tap once → +1 clap, subtle bump animation.
 *   • Repeated taps within 400ms accumulate; each tap sends +1 to backend.
 *   • Long press (or repeated taps) allowed up to 50 per fan (MAX_CLAPS_PER_FAN).
 *   • Anonymous users: prompted to sign in.
 *
 * Backend: POST /api/publisher/v1/articles/{id}/clap  {claps: N}
 * Read-back: GET /api/publisher/v1/articles/{id}/engagement
 */
import { useCallback, useEffect, useRef, useState } from "react";
import { Heart } from "lucide-react";

const BASE = `${process.env.REACT_APP_BACKEND_URL || ""}/api/publisher/v1`;

function getToken() {
  if (typeof window === "undefined") return null;
  return window.localStorage.getItem("sp_token") || window.localStorage.getItem("token");
}

export default function ClapButton({ articleId }) {
  const [state, setState] = useState({
    total: 0, my: 0, max: 50, loading: true, bumping: false,
  });
  const pendingRef = useRef(0);
  const timerRef = useRef(null);

  const load = useCallback(async () => {
    try {
      const token = getToken();
      const r = await fetch(`${BASE}/articles/${articleId}/engagement`, {
        headers: token ? { Authorization: `Bearer ${token}` } : {},
      });
      if (!r.ok) throw new Error();
      const d = await r.json();
      setState((s) => ({
        ...s,
        total: d.claps_total || 0,
        my: d.my_claps || 0,
        max: d.max_claps || 50,
        loading: false,
      }));
    } catch {
      setState((s) => ({ ...s, loading: false }));
    }
  }, [articleId]);

  useEffect(() => { load(); }, [load]);

  const flushPending = useCallback(async () => {
    const n = pendingRef.current;
    if (!n) return;
    pendingRef.current = 0;
    const token = getToken();
    if (!token) return;
    try {
      const r = await fetch(`${BASE}/articles/${articleId}/clap`, {
        method: "POST",
        headers: { "Content-Type": "application/json", Authorization: `Bearer ${token}` },
        body: JSON.stringify({ claps: Math.min(n, 50) }),
      });
      if (r.ok) {
        const d = await r.json();
        // Reload total after write to get server-authoritative count.
        setState((s) => ({ ...s, my: d.my_claps || s.my }));
        await load();
      }
    } catch { /* silent — optimistic UI already updated */ }
  }, [articleId, load]);

  const onClap = useCallback(() => {
    const token = getToken();
    if (!token) {
      window.location.href = "/login?next=" + encodeURIComponent(window.location.pathname);
      return;
    }
    if (state.my >= state.max) return;
    // Optimistic update.
    setState((s) => ({
      ...s,
      my: Math.min(s.my + 1, s.max),
      total: s.total + 1,
      bumping: true,
    }));
    pendingRef.current += 1;
    setTimeout(() => setState((s) => ({ ...s, bumping: false })), 180);
    if (timerRef.current) clearTimeout(timerRef.current);
    timerRef.current = setTimeout(flushPending, 500);
  }, [state.my, state.max, flushPending]);

  // Flush on unmount.
  useEffect(() => {
    return () => { if (pendingRef.current) flushPending(); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const capped = state.my >= state.max;

  return (
    <div
      className="inline-flex items-center gap-3 select-none"
      data-testid="clap-button-container"
    >
      <button
        type="button"
        onClick={onClap}
        disabled={state.loading || capped}
        aria-label={capped ? "Maximum claps given" : "Clap for this article"}
        title={capped ? "You've clapped 50 times — thank you!" : "Tap to clap"}
        className={`group relative inline-flex items-center justify-center w-14 h-14 rounded-full border-2 transition-all ${
          state.my > 0
            ? "border-rose-500 bg-rose-50 text-rose-600 hover:bg-rose-100"
            : "border-slate-300 bg-white text-slate-600 hover:border-rose-400 hover:text-rose-500"
        } ${state.bumping ? "scale-110" : "scale-100"} disabled:opacity-60 disabled:cursor-not-allowed`}
        data-testid="clap-button"
      >
        <Heart
          className={`w-6 h-6 transition-all ${state.my > 0 ? "fill-current" : ""} ${state.bumping ? "scale-125" : ""}`}
        />
        {state.my > 0 && (
          <span
            className="absolute -top-1 -right-1 bg-rose-600 text-white text-[10px] font-bold rounded-full min-w-[20px] h-5 px-1 flex items-center justify-center border-2 border-white"
            data-testid="clap-my-count"
          >
            {state.my}
          </span>
        )}
      </button>
      <div className="flex flex-col leading-tight">
        <span
          className="text-lg font-bold text-slate-900"
          data-testid="clap-total-count"
        >
          {state.total.toLocaleString()}
        </span>
        <span className="text-[11px] uppercase tracking-widest text-slate-500 font-semibold">
          {state.total === 1 ? "clap" : "claps"}
        </span>
      </div>
    </div>
  );
}
